import { CircularProgress, IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Dispatch, SetStateAction, useState } from 'react'
import CustomInput from '../TextInput'
import CustomButton from '../Button'
import JobService from '../../services/JobService'
import { useSnackbar } from 'notistack'
import {
    translateEnumFrequencyToString,
    translateEnumJobPeriodToString,
    translateStringToEnumFrequency,
    translateStringToEnumJobPeriod,
    validateNumber,
} from '../../helpers/utils'
import CustomDropdown from '../Dropdown'
import { Currency, Frequency, JobPeriod, Salary } from '../../models/post'
import CustomIconButton from '../IconButton'

interface Props {
    postId: string
    handleClose: () => void
    setMutate: Dispatch<SetStateAction<boolean>>
}

const DEFAULT_FILE_NAME = 'Ningún archivo seleccionado (únicamente PDF)'

export const currencyOptions = [
    Currency.ARS,
    Currency.BRL,
    Currency.EUR,
    Currency.MXN,
    Currency.USD,
]

export const frequencyOptions = [
    Frequency.HOURLY,
    Frequency.WEEKLY,
    Frequency.MONTHLY,
    Frequency.YEARLY,
].map((frequency) => translateEnumFrequencyToString(frequency))

export const workTypeOptions = [JobPeriod.INTERNSHIP, JobPeriod.PART_TIME, JobPeriod.FULL_TIME].map(
    (job) => translateEnumJobPeriodToString(job),
)

const PostulationModal = ({ postId, handleClose, setMutate }: Props) => {
    const [salary, setSalary] = useState<Salary>({
        amount: 0,
        currency: Currency.ARS,
        period: JobPeriod.INTERNSHIP,
        frequency: Frequency.HOURLY,
    })
    const [fileName, setFileName] = useState({
        profile: DEFAULT_FILE_NAME,
        cv: DEFAULT_FILE_NAME,
    })
    const [files, setFiles] = useState<{ profile: File | null; cv: File | null }>({
        profile: null,
        cv: null,
    })
    const [errors, setErrors] = useState<{ cv: string; profile: string; salary: string }>({
        cv: '',
        profile: '',
        salary: '',
    })
    const { enqueueSnackbar } = useSnackbar()
    const [pending, setPending] = useState<boolean>(false)

    const handleFileChange = (name: string, value: string, file: File) => {
        if (file) {
            setFiles((prev) => ({ ...prev, [name]: file }))
            setFileName((prev) => ({ ...prev, [name]: file.name }))
        } else {
            setFileName((prev) => ({ ...prev, [name]: value }))
        }
    }

    const validateInfo = () => {
        const newErrors = {
            cv:
                fileName.cv === DEFAULT_FILE_NAME && fileName.profile === DEFAULT_FILE_NAME
                    ? 'Completá este campo'
                    : '',
            profile:
                fileName.cv === DEFAULT_FILE_NAME && fileName.profile === DEFAULT_FILE_NAME
                    ? 'Completá este campo'
                    : '',
            salary:
                !!salary && !validateNumber(salary.amount.toString())
                    ? 'El monto no es válido'
                    : '',
        }

        setErrors(newErrors)

        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return
        setPending(true)

        const response = await new JobService().postulateJob(
            postId,
            files as { cv: File; profile: File },
            salary,
        )

        setPending(false)

        if (response.status === 200) {
            setMutate((prevMutate) => !prevMutate)

            enqueueSnackbar('Postulación enviada', {
                variant: 'success',
                autoHideDuration: 6000,
            })
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        handleClose()
    }

    const disableSubmit = () => files.cv === null && files.profile === null

    return (
        <div className="flex flex-col w-[60vw] md:w-[35vw] overflow-auto max-h-[50vh] md:max-h-[65vh] px-1 md:px-2 gap-2 md:gap-4 text-error">
            <div className="flex justify-between items-center">
                <label
                    className="text-secondary font-semibold"
                    htmlFor="postInput"
                >
                    Postulación
                </label>
                <div className="flex items-center">
                    <IconButton
                        className="text-primary"
                        onClick={handleClose}
                    >
                        <ClearIcon />
                    </IconButton>
                </div>
            </div>
            <div className="flex flex-col gap-2 md:gap-4">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                        <input
                            type="file"
                            name="cv"
                            id="cv-upload"
                            accept="application/pdf"
                            onChange={(e) => {
                                const files = e.target.files
                                if (files && files.length > 0) {
                                    handleFileChange(e.target.name, e.target.value, files[0])
                                }
                                e.currentTarget.value = ''
                            }}
                            hidden
                            className="hidden"
                        />
                        <label
                            htmlFor="cv-upload"
                            className="px-2 py-2 bg-primary text-white rounded cursor-pointer items-center"
                        >
                            Subir CV
                        </label>
                        <span
                            id="file-name"
                            className={`${fileName.cv === DEFAULT_FILE_NAME ? 'text-error' : 'text-linkedin'} text-xs md:text-sm ml-3`}
                        >
                            {fileName.cv}
                            {fileName.cv !== DEFAULT_FILE_NAME && (
                                <CustomIconButton
                                    icon={<ClearIcon />}
                                    onClick={() => {
                                        setFileName((prev) => ({ ...prev, cv: DEFAULT_FILE_NAME }))
                                        setFiles((prev) => ({ ...prev, cv: null }))
                                    }}
                                    className="p-0 ml-1"
                                />
                            )}
                        </span>
                    </div>
                    {errors.profile !== '' && <span className="text-error">{errors.profile}</span>}
                </div>
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                        <input
                            type="file"
                            name="profile"
                            id="profile-upload"
                            accept="application/pdf"
                            onChange={(e) => {
                                const files = e.target.files
                                if (files && files.length > 0) {
                                    handleFileChange(e.target.name, e.target.value, files[0])
                                }
                                e.currentTarget.value = ''
                            }}
                            hidden
                            className="hidden"
                        />
                        <label
                            htmlFor="profile-upload"
                            className="px-2 py-2 bg-primary text-white text-sm md:text-base rounded cursor-pointer items-center whitespace-nowrap"
                        >
                            Subir perfil
                        </label>
                        <span
                            id="file-name"
                            className={`${fileName.profile === DEFAULT_FILE_NAME ? 'text-error' : 'text-linkedin'} text-xs md:text-sm ml-1 md:ml-3`}
                        >
                            {fileName.profile}
                            {fileName.profile !== DEFAULT_FILE_NAME && (
                                <CustomIconButton
                                    icon={<ClearIcon />}
                                    onClick={() => {
                                        setFileName((prev) => ({
                                            ...prev,
                                            profile: DEFAULT_FILE_NAME,
                                        }))
                                        setFiles((prev) => ({ ...prev, profile: null }))
                                    }}
                                    className="p-0 ml-1"
                                />
                            )}
                        </span>
                    </div>
                    {errors.profile !== '' && (
                        <span className="text-error text-sm md:text-base">{errors.profile}</span>
                    )}
                    <span className="text-primary text-xs md:text-sm">
                        {
                            '(Podés descargar tu información cargada en la aplicación desde tu perfil)'
                        }
                    </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 mt-2 md:mt-3 gap-2 md:gap-3">
                    <CustomDropdown
                        className="col-span-1 w-full"
                        label="Tipo de Moneda"
                        name="currency"
                        value={salary?.currency ?? ''}
                        onChange={(e) =>
                            setSalary((prev) => ({ ...prev, currency: e.target.value as Currency }))
                        }
                        data={currencyOptions}
                    />
                    <CustomDropdown
                        className="col-span-1 w-full"
                        label="Jornada Laboral"
                        name="jobPeriod"
                        value={translateEnumJobPeriodToString(salary.period) ?? ''}
                        onChange={(e) => {
                            setSalary((prev) => ({
                                ...prev,
                                period: translateStringToEnumJobPeriod(e.target.value as string),
                            }))
                        }}
                        data={workTypeOptions}
                    />
                    <CustomDropdown
                        className="col-span-1 w-full"
                        label="Frecuencia de pago"
                        name="frequency"
                        value={translateEnumFrequencyToString(salary.frequency) ?? ''}
                        onChange={(e) => {
                            setSalary((prev) => ({
                                ...prev,
                                frequency: translateStringToEnumFrequency(
                                    e.target.value as string,
                                ) as Frequency,
                            }))
                        }}
                        data={frequencyOptions}
                    />
                    <CustomInput
                        label="Salario pretendido (opcional)"
                        placeholder="Ingresá un monto pretendido"
                        name="salary"
                        className="w-full col-span-1 md:col-span-3"
                        onChange={(e) =>
                            setSalary((prev) => ({ ...prev, amount: parseInt(e.target.value) }))
                        }
                        value={salary.amount === 0 ? '' : salary.amount}
                        errorText={errors.salary}
                    />
                </div>
                <CustomButton
                    onClick={handleSubmit}
                    disabled={disableSubmit() || pending}
                >
                    {!pending ? (
                        'Enviar'
                    ) : (
                        <CircularProgress
                            className="text-primary"
                            size={24}
                        />
                    )}
                </CustomButton>
            </div>
        </div>
    )
}

export default PostulationModal
